import { PricingSettings } from '../services/settings.service';

/**
 * Basic price breakdown interface for material cost-based calculation
 */
export interface PriceBreakdown {
  materialTotal: number;
  cutTotal: number;
  setupCost: number;
  preMarginSubtotal: number;
  marginAmount: number;
  subtotalWithMargin: number;
  taxAmount: number;
  subtotalWithTax: number;
  vendorCommissionAmount: number;
  managerCommissionAmount: number;
  finalPrice: number;
}

/**
 * Extended price breakdown interface for dimension-based calculation
 * Uses the backend formula: baseCosts / (1 - variablesSum)
 */
export interface DimensionBasedPriceBreakdown {
  materialCostPerUnit: number;
  materialCost: number;
  quantity: number;
  units: number;
  cutCost: number;
  setupCost: number;
  baseCost: number;
  marginRate: number;
  marginAmount: number;
  taxRate: number;
  taxAmount: number;
  vendorCommission: number;
  vendorCommissionAmount: number;
  managerCommission: number;
  managerCommissionAmount: number;
  finalPrice: number;
  pricePerUnit: number;
  variablesSum: number;
}

/**
 * Test calculator state interface (matches useSettings testCalc state)
 */
export interface TestCalcState {
  materialCost: string;
  quantity: string;
  units: string;
  width: string;
  height: string;
  thickness: string;
  density: string;
  pricePerKg: string;
}

/**
 * Calculate material cost from physical dimensions.
 * Formula: Width(cm/100=m) x Height(cm/100=m) x Thickness(mm/1000) x Density(g/cm^3 * 1000 = kg/m^3) x PricePerKg(R$/kg)
 */
export function calculateMaterialCostFromDimensions(
  widthCm: number,
  heightCm: number,
  thicknessMm: number,
  densityGperCm3: number,
  pricePerKg: number
): number {
  return (widthCm / 100) * (heightCm / 100) * (thicknessMm / 1000) * (densityGperCm3 * 1000) * pricePerKg;
}

/**
 * Parse margin tiers string format: "1-5:0.35, 6-10:0.32, >10:0.30"
 * Returns the effective margin rate for the given unit count.
 */
export function getEffectiveMarginRate(
  units: number,
  marginTiersStr: string | null,
  defaultMarginRate: number
): number {
  if (!marginTiersStr) {
    return defaultMarginRate;
  }

  try {
    const segments = marginTiersStr.split(',').map((s) => s.trim()).filter((s) => s.length > 0);

    for (const segment of segments) {
      const [rangePart, ratePart] = segment.split(':').map((s) => s.trim());
      const rate = parseFloat(ratePart);

      if (rangePart.startsWith('>')) {
        // Open-ended range: >10
        const threshold = parseInt(rangePart.substring(1).trim());
        if (units > threshold) {
          return rate;
        }
      } else if (rangePart.includes('-')) {
        // Range: 1-5
        const [minStr, maxStr] = rangePart.split('-').map((s) => s.trim());
        const minQty = parseInt(minStr);
        const maxQty = parseInt(maxStr);

        if (units >= minQty && units <= maxQty) {
          return rate;
        }
      }
    }
  } catch {
    // Keep default marginRate if parsing fails
  }

  return defaultMarginRate;
}

/**
 * Calculate price breakdown using dimensions and the backend formula.
 * This is the formula used in the Settings page price calculator.
 *
 * Formula: baseCosts / (1 - variablesSum)
 * Where variablesSum = marginRate + taxRate + vendorCommission + managerCommission
 */
export function calculateDimensionBasedPriceBreakdown(
  testCalc: TestCalcState,
  pricingSettings: PricingSettings,
  pricingInputs: Record<string, string>
): DimensionBasedPriceBreakdown {
  // Parse dimension inputs
  const width = parseFloat(testCalc.width);
  const height = parseFloat(testCalc.height);
  const thickness = parseFloat(testCalc.thickness);
  const density = parseFloat(testCalc.density);
  const pricePerKg = parseFloat(testCalc.pricePerKg);

  // Calculate material cost from dimensions if all values are provided
  let materialCostPerUnit = 0;
  if (width && height && thickness && density && pricePerKg) {
    materialCostPerUnit = calculateMaterialCostFromDimensions(
      width,
      height,
      thickness,
      density,
      pricePerKg
    );
  } else {
    // Otherwise use the direct material cost input
    materialCostPerUnit = parseFloat(testCalc.materialCost) || 0;
  }

  const units = parseFloat(testCalc.units) || 1;

  // Get pricing parameters from inputs or settings
  const cutCostPerUnit = parseFloat(
    pricingInputs.cutCostPerUnit || pricingSettings.cutCostPerUnit.toString()
  ) || 0;
  const setupCost = parseFloat(
    pricingInputs.setupCostPerOrder || pricingSettings.setupCostPerOrder.toString()
  ) || 0;
  const marginRate = parseFloat(
    pricingInputs.marginRate || pricingSettings.marginRate.toString()
  ) || 0;
  const taxRate = parseFloat(
    pricingInputs.taxRate || pricingSettings.taxRate.toString()
  ) || 0;
  const vendorCommission = parseFloat(
    pricingInputs.vendorCommission || pricingSettings.vendorCommission.toString()
  ) || 0;
  const managerCommission = parseFloat(
    pricingInputs.managerCommission || pricingSettings.managerCommission.toString()
  ) || 0;

  // Calculate base costs - material cost must be multiplied by units (FIX: was using quantity=1)
  const materialTotal = materialCostPerUnit * units;
  const cutTotal = cutCostPerUnit * units;
  const baseCost = materialTotal + cutTotal + setupCost;

  // Determine effective margin rate (use tiered if available)
  const effectiveMarginRate = getEffectiveMarginRate(
    units,
    pricingSettings.marginTiers,
    marginRate
  );

  // Apply formula: baseCosts / (1 - variablesSum)
  // This is the same formula used by the backend API
  const variablesSum = effectiveMarginRate + taxRate + vendorCommission + managerCommission;
  const finalPrice = baseCost / (1 - variablesSum);
  const marginAmount = finalPrice * effectiveMarginRate;

  // Calculate individual variable amounts for breakdown display
  const taxAmount = finalPrice * taxRate;
  const vendorCommissionAmount = finalPrice * vendorCommission;
  const managerCommissionAmount = finalPrice * managerCommission;

  const pricePerUnit = units > 0 ? finalPrice / units : 0;

  return {
    materialCostPerUnit,
    materialCost: materialTotal,
    quantity: units, // Keep for backwards compatibility, same as units
    units,
    cutCost: cutTotal,
    setupCost,
    baseCost,
    marginRate: effectiveMarginRate,
    marginAmount,
    taxRate,
    taxAmount,
    vendorCommission,
    vendorCommissionAmount,
    managerCommission,
    managerCommissionAmount,
    finalPrice,
    pricePerUnit,
    variablesSum,
  };
}

/**
 * Calculate price breakdown from direct material cost (legacy method).
 * This calculates prices by adding margins on top of costs.
 */
export function calculatePriceBreakdown(
  materialCostStr: string,
  quantityStr: string,
  unitsStr: string,
  pricingSettings: PricingSettings,
  pricingInputs: Record<string, string>
): PriceBreakdown {
  const materialCost = parseFloat(materialCostStr) || 0;
  const quantity = parseFloat(quantityStr) || 1;
  const units = parseFloat(unitsStr) || 1;

  const cutCost = parseFloat(
    pricingInputs.cutCostPerUnit || pricingSettings.cutCostPerUnit.toString()
  ) || 0;
  const setupCost = parseFloat(
    pricingInputs.setupCostPerOrder || pricingSettings.setupCostPerOrder.toString()
  ) || 0;
  const taxRate = parseFloat(
    pricingInputs.taxRate || pricingSettings.taxRate.toString()
  ) || 0;
  const vendorCommission = parseFloat(
    pricingInputs.vendorCommission || pricingSettings.vendorCommission.toString()
  ) || 0;
  const managerCommission = parseFloat(
    pricingInputs.managerCommission || pricingSettings.managerCommission.toString()
  ) || 0;

  const materialTotal = materialCost * quantity * units;
  const cutTotal = cutCost * units * quantity;
  const preMarginSubtotal = materialTotal + cutTotal + setupCost;

  // Calculate tiered margin or use fixed margin
  let marginAmount = 0;
  const marginTiers = pricingSettings.marginTiers;

  if (marginTiers) {
    try {
      const tiers = JSON.parse(marginTiers);
      if (Array.isArray(tiers)) {
        for (let i = tiers.length - 1; i >= 0; i--) {
          const tier = tiers[i];
          if (preMarginSubtotal >= tier.min) {
            marginAmount = preMarginSubtotal * tier.rate;
            break;
          }
        }
      }
    } catch {
      marginAmount = preMarginSubtotal * (parseFloat(
        pricingInputs.marginRate || pricingSettings.marginRate.toString()
      ) || 0);
    }
  } else {
    marginAmount = preMarginSubtotal * (parseFloat(
      pricingInputs.marginRate || pricingSettings.marginRate.toString()
    ) || 0);
  }

  const subtotalWithMargin = preMarginSubtotal + marginAmount;
  const taxAmount = subtotalWithMargin * taxRate;
  const subtotalWithTax = subtotalWithMargin + taxAmount;
  const vendorCommissionAmount = subtotalWithTax * vendorCommission;
  const managerCommissionAmount = subtotalWithTax * managerCommission;
  const finalPrice = subtotalWithTax + vendorCommissionAmount + managerCommissionAmount;

  return {
    materialTotal,
    cutTotal,
    setupCost,
    preMarginSubtotal,
    marginAmount,
    subtotalWithMargin,
    taxAmount,
    subtotalWithTax,
    vendorCommissionAmount,
    managerCommissionAmount,
    finalPrice,
  };
}

/**
 * Normalize phone number to E.164 format (add + prefix if missing)
 */
export function normalizePhoneE164(phone: string): string {
  const cleaned = phone.trim();
  if (!cleaned) return cleaned;
  return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
}
